import { CommonModule } from "@angular/common";
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  inject,
  signal,
  viewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { CategoryRecord, PageRequestDTO } from "b2b-ecommerce-lib";
import { MenuItem } from "primeng/api";
import { BadgeModule } from "primeng/badge";
import { ButtonDirective } from "primeng/button";
import { DropdownChangeEvent } from "primeng/dropdown";
import { IconField } from "primeng/iconfield";
import { InputIcon } from "primeng/inputicon";
import { InputTextModule } from "primeng/inputtext";
import { ListboxModule } from "primeng/listbox";
import { Menu, MenuModule } from "primeng/menu";
import { OverlayBadgeModule } from "primeng/overlaybadge";
import { Select } from "primeng/select";
import { SplitButton } from "primeng/splitbutton";
import { TooltipModule } from "primeng/tooltip";
import { Subscription, filter, of, switchMap } from "rxjs";
import { CartService } from "../../services/cart.service";
import { CatalogService } from "../../services/catalog.service";
import { HeaderService } from "../../services/header.service";
import { SessionService } from "../../services/login.service";
import { ThemeService } from "../../services/theme.service";
import { I18nService } from "../../utils/translate.service";
import { ToastService } from "../common-services/toast.service";

type AppTheme = "LIGHT" | "DARK";

interface Language {
  name: string;
  code: string;
}
@Component({
  selector: "app-header",
  imports: [
    CommonModule,
    Select,
    SplitButton,
    FormsModule,
    MenuModule,
    TranslateModule,
    ButtonDirective,
    InputTextModule,
    InputIcon,
    IconField,
    OverlayBadgeModule,
    BadgeModule,
    ListboxModule,
    RouterLink,
    TooltipModule,
  ],
  templateUrl: "./header.component.html",
  styleUrl: "./header.component.scss",
})
export class HeaderComponent implements OnDestroy {
  @Input() defaultLanguage: string | null = "";
  @Output() selectedLanguageEmitter = new EventEmitter<string>();
  @ViewChild(Menu) menuRef!: Menu;

  themeService = inject(ThemeService);
  translateService = inject(I18nService);
  sessionService = inject(SessionService);
  router = inject(Router);
  cartService = inject(CartService);
  catalogService = inject(CatalogService);
  headerService = inject(HeaderService);
  toastService = inject(ToastService);

  categoriesPaginator: PageRequestDTO = {
    page: 0,
    size: 10,
  };

  localTheme: AppTheme | null = null;
  isMobileSize: boolean = false;
  isMenuVisible: boolean = false;
  categorie: CategoryRecord[] = [];
  categoryOptions: MenuItem[] = [];

  languages: Language[] = [
    { name: "IT", code: "IT" },
    { name: "ENG", code: "EN" },
  ];
  selectedLanguage: Language | undefined = undefined;

  accountOptions: MenuItem[] = [];

  readonly selectedCategory = signal<MenuItem | undefined>(undefined);
  readonly searchFilter = signal("");
  private inputSearch = viewChild<ElementRef>("inputSearch");
  private readonly route = inject(ActivatedRoute);
  private subs: Subscription[] = [];

  constructor() {
    this.localTheme = localStorage.getItem("currentTheme") as AppTheme;

    this.subs.push(
      this.headerService.getIsMobile().subscribe((isMobile) => {
        this.isMobileSize = isMobile;
        this.isMenuVisible = false;
        if (this.menuRef?.visible && !isMobile) {
          this.menuRef.hide();
        }
      }),
    );

    this.subs.push(
      this.route.queryParams.subscribe((params) => {
        const search = params["search"];
        if (search) {
          this.searchFilter.set(search);
        } else {
          this.searchFilter.set("");
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((x) => x.unsubscribe());
  }

  ngOnInit(): void {
    const startingLanguage = this.languages.find(
      (x) => x.code.toLowerCase() === this.defaultLanguage,
    );
    this.selectedLanguage = startingLanguage;
    this.selectedLanguageEmitter.emit(startingLanguage!.code);
    this.subs.push(
      this.sessionService.isAuthenticated$.subscribe((authed) => {
        this.accountOptions = this.buildMenu(authed);
      }),
    );

    this.subs.push(
      this.catalogService.getAllCategories().subscribe({
        next: (response) => {
          this.categorie = response ?? [];
          this.categoryOptions = [
            ...this.categoryOptions,
            ...this.categorie.map((item) => ({
              label: item.name ?? "",
              id: item.uuid ?? "",
              command: (event: { item: MenuItem }) => {
                this.selectCategory(event.item);
              },
            })),
          ];
        },
        error: (error) => {
          console.error("Error loading categories:", error);
        },
      }),
    );
    this.subs.push(
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          switchMap(() => {
            const activeRoute = this.route.root.firstChild;
            if (activeRoute) {
              return activeRoute.paramMap;
            }
            return of(null);
          }),
        )
        .subscribe((params) => {
          const idCategory = params?.get("idCategory");
          if (idCategory) {
            this.selectedCategory.set(this.categoryOptions.find((x) => x.id === idCategory));
          } else {
            this.selectedCategory.set(undefined);
          }
        }),
    );
  }

  buildMenu(isLogged: boolean): MenuItem[] {
    this.translateService.selectedLanguage$.subscribe((language) => {
      let allCategory;
      if (language == "it") {
        allCategory = {
          label: "Tutte le categorie",
          id: "all",
          command: (event: any) => {
            this.selectCategory(event.item);
          },
        };
      } else {
        allCategory = {
          label: "All categories",
          id: "all",
          command: (event: any) => {
            this.selectCategory(event.item);
          },
        };
      }

      this.categoryOptions = [
        allCategory,
        ...this.categorie.map((item) => ({
          label: item.name ?? "",
          id: item.uuid ?? "",
          command: (event: { item: MenuItem }) => {
            this.selectCategory(event.item);
          },
        })),
      ];
    });

    let menu = [{}];
    if (!isLogged) return [];
    menu = [
      {
        label: "account.MyAccount",
        icon: "pi pi-chevron-right",
        command: () => {
          this.router.navigateByUrl(`/account`);
          if (this.isMenuVisible) {
            this.toggleHamburgerMenu();
          }
        },
      },
      {
        label: "account.Courses",
        icon: "pi pi-chevron-right",
        command: () => {
          this.router.navigateByUrl(`/account/courses`);
          if (this.isMenuVisible) {
            this.toggleHamburgerMenu();
          }
        },
      },
      {
        label: "Logout",
        icon: "pi pi-sign-out",
        command: async () => {
          await this.sessionService.logout(true, true);
          if (this.isMenuVisible) {
            this.toggleHamburgerMenu();
          }
        },
      },
    ];

    return menu;
  }

  switchTheme() {
    const element = document.querySelector("html");
    if (element) {
      element.classList.toggle("dark");
    }

    const theme: AppTheme = localStorage.getItem("currentTheme") as AppTheme;
    const nextTheme: AppTheme = theme === "DARK" ? "LIGHT" : "DARK";
    this.localTheme = nextTheme;
    this.themeService.setCssTheme(nextTheme);
  }

  selectLanguage(event: DropdownChangeEvent) {
    const language: Language = event.value as Language;
    this.selectedLanguageEmitter.emit(language.code);
    if (this.isMenuVisible) {
      this.toggleHamburgerMenu();
    }
  }

  searchWithQuery() {
    if (this.searchFilter()) {
      const hasParamFilter = this.route.snapshot.queryParamMap.has("filter");
      const params: { search?: string; filter?: string } = {};
      params.search = this.searchFilter();
      if (hasParamFilter) {
        params.filter = this.route.snapshot.queryParamMap.get("filter") ?? "";
      }
      this.router.navigate([this.buildCategoryRoute()], {
        queryParams: params,
      });
    }
  }

  clearQuery(): void {
    this.searchFilter.set("");
    if (this.router.url.includes("home")) return;
  }

  selectCategory(item: MenuItem | undefined) {
    setTimeout(() => this.inputSearch()?.nativeElement?.focus(), 0);
    if (!item) return;
    this.selectedCategory.set(item);
  }

  private buildCategoryRoute(): string {
    const category = this.selectedCategory();
    if (category) {
      return "/category/" + category.id;
    } else {
      return "/category/all";
    }
  }

  goToHome() {
    this.router.navigateByUrl("home");
    if (this.isMenuVisible) {
      this.toggleHamburgerMenu();
    }
  }

  goToCart() {
    this.router.navigateByUrl("cart");
    if (this.isMenuVisible) {
      this.toggleHamburgerMenu();
    }
  }

  toggleHamburgerMenu() {
    this.isMenuVisible = !this.isMenuVisible;
  }
}
